import { CodetableMetadata } from 'app/area/codetable/types-store';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { CreateCodeItemProps, LoadCodeOptions, CodeActionType } from 'app/area/codetable/item/types';
import { endpoints } from 'app/area/codetable/item/endpoints';
import { mapCodeToStore } from 'app/area/codetable/item/mapping/map-code-to-store';
import { api } from './api';
import { ComplexValue } from 'app/infrastructure/ui/form/types';
import { CodeItem } from 'app/area/codetable/item/types-store';
import { CodetableOptions } from 'app/area/codetable/common/codetable-options-handler/types';
import { substanceCodetableIds, userGroupCodetableId } from 'app/area/codetable/item/constants';
import { createParent } from 'app/area/codetable/item/mapping/create-empty-code-item';

export const loadAllOptions = async (props: CreateCodeItemProps, metadata: CodetableMetadata) => {
  const { loadCodetableOptions, parentId, loadOptions } = props;
  const codetableId = !!metadata && !!metadata.codetable && isDefinedIncludingZero(metadata.codetable.id)
    ? metadata.codetable.id
    : undefined;

  await Promise.all([
    shouldLoadParentOptions(parentId) && loadCodetableOptions('parent', codetableId, { parentId }),
    shouldLoadUserGroups(metadata) && loadOptions('userGroups', endpoints.userGroup, { optionsCollectionId: codetableId }),
    shouldLoadCaseTypes(metadata) && loadOptions('caseTypes', endpoints.caseType, undefined),
    shouldLoadUomSets(codetableId, props.actionType) && loadCodetableOptions('uomSets',
      substanceCodetableIds.uomSets, { includeNotSelected: false }),
    loadLanguages(metadata, loadOptions)
  ]);
};

export const loadLanguages = (metadata: CodetableMetadata, loadOptions: LoadCodeOptions) =>
  shouldLoadDescriptions(metadata) && loadOptions('languages', endpoints.language, undefined);

export const getPath = (parentId: number, codetableOptions: { [key: string]: CodetableOptions }): ComplexValue<number>[] => {

  if (isDefinedIncludingZero(parentId)) {
    const codes = !!codetableOptions
      && !!codetableOptions.parent
      && !!codetableOptions.parent.codes
      && codetableOptions.parent.codes.length > 0
      ? codetableOptions.parent.codes
      : undefined;

    if (codes) {
      const findParent = (parentCodes: ComplexValue<number>[], path: ComplexValue<number>[] = []) => {
        if (parentCodes[0].value === parentId) {
          return [...path, parentCodes[0]];
        } else if (!parentCodes[0].descendants || parentCodes[0].descendants.length === 0) {
          return undefined;
        }
        return findParent(parentCodes[0].descendants, [...path, parentCodes[0]]);
      };

      return findParent(codes);
    }
  }

  return undefined;
};

export const getParent = (parentId: number, codetableOptions: { [key: string]: CodetableOptions }): ComplexValue<number> => {

  const path = getPath(parentId, codetableOptions);

  return path && path.length ? path[path.length - 1] : undefined;
};

export const getParentFullPath = (parentId: number, codetableOptions: { [key: string]: CodetableOptions }): string => {

  const path = getPath(parentId, codetableOptions) || [];

  return path.reduce((result: string, item: ComplexValue<number>) => result
    ? result + ' - ' + item.description
    : item.description, '');
};

export const getCategory = (category: ComplexValue<number>, props: CreateCodeItemProps, metadata: CodetableMetadata) => {
  const { breadcrumbs, parentId } = props;

  if (!shouldGetCategory(metadata)) {
    return undefined;
  }

  if (!!category && isDefinedIncludingZero(category.value)) {
    return category;
  }

  if (isDefinedIncludingZero(parentId)) {
    const codetableId = breadcrumbs && breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1] : undefined;
    return loadParentCategory(codetableId, parentId);
  }

  return undefined;
};

export const getUserLanguageTranslation = (textsLanguage: number, metadata: CodetableMetadata,
  codeItem: CodeItem, property: 'description' | 'longDescription'): string => {
  if (!metadata || !metadata.features || !metadata.features[property]) {
    return undefined;
  }

  const userLanguageDescription = codeItem && codeItem[property]
    && codeItem[property].find(description => description.value === textsLanguage);

  return userLanguageDescription ? userLanguageDescription.description : undefined;
};

export const createCodeItem = (
  actionType: CodeActionType,
  codeItem: CodeItem,
  metadata: CodetableMetadata,
  parent: ComplexValue<number>,
  parentId: number
): CodeItem => {
  if (!codeItem) {
    return undefined;
  }

  return {
    [CodeActionType.Duplicate]: {
      ...codeItem,
      ...(!!codeItem.longDescription && ({
        longDescription: codeItem.longDescription.map(item => ({ value: item.value, description: '' }))
      })),
      ...(!!codeItem.description && ({
        description: codeItem.description.map(item => ({ value: item.value, description: '' }))
      })),
      ...(!!codeItem.companyRegisterId && ({
        companyRegisterId: ''
      })),
      ...(!!codeItem.userGroups && ({
        userGroups: codeItem.userGroups.filter(item => !item.readOnly)
      })),
      ...(isDefinedIncludingZero(parentId) && {
        parent: createParent(metadata, parent, parentId)
      })
    }
  }[actionType] || codeItem;
};

export const shouldCreateCodetableItem = (codeId: number, actionType: CodeActionType) => {
  return actionType === CodeActionType.Duplicate || (!codeId && codeId !== 0);
};

export const codetableItemEndpoint = (actionType: CodeActionType, codetableId: number, codeId: number) => {
  if (!actionType || !(Object.values(CodeActionType).includes(actionType)) || !codetableId) {
    return undefined;
  }

  const customEndpoints = {
    [CodeActionType.Duplicate]: {
      [substanceCodetableIds.uomSets]: endpoints.duplicateCode(codetableId, codeId),
      [userGroupCodetableId]: endpoints.duplicateCode(codetableId, codeId)
    }
  }[actionType];

  return (customEndpoints && customEndpoints[codetableId]) || endpoints.code(codetableId);
};

const shouldGetCategory = (metadata: CodetableMetadata) =>
  !!metadata && !!metadata.codetableCategory && isDefinedIncludingZero(metadata.codetableCategory.id);

const shouldLoadParentOptions = (parentId: number) => isDefinedIncludingZero(parentId);

const shouldLoadUserGroups = (metadata: CodetableMetadata) => !!metadata && !!metadata.features && !!metadata.features.userGroup;

const shouldLoadCaseTypes = (metadata: CodetableMetadata) => !!metadata && !!metadata.features && !!metadata.features.caseType;

const shouldLoadDescriptions = (metadata: CodetableMetadata) => !!metadata && !!metadata.features && !!metadata.features.description;

const shouldLoadUomSets = (codetableId: number, actionType: CodeActionType) => codetableId === substanceCodetableIds.hierarchy
  && actionType === CodeActionType.Add;

const loadParentCategory = async (codetableId: number, parentId: number) => {
  const codeItem = await api.loadCodeItem(codetableId, parentId);

  const code = mapCodeToStore(codeItem);

  return !!code ? code.category : undefined;
};
